import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"

import { Card, CardContent, ErrorState, PageHeader, Skeleton } from "@/components/ui"
import { useOccupation } from "@/features/ofo/api"
import { AnnotationsPanel } from "@/features/ofo/annotations-panel"
import { RegisterPanel } from "@/features/ofo/register-panel"
import { useSession } from "@/lib/session"

export const Route = createFileRoute("/ofo/$occupationId")({ component: OccupationScreen })

function OccupationScreen() {
  const { occupationId } = Route.useParams()
  const { canWrite } = useSession()
  // The id is a server-issued integer. A path that is not one is a bad link,
  // not a request worth making.
  const id = Number(occupationId)
  const query = useOccupation(id)
  const occupation = query.data

  return (
    <>
      <Link
        to="/ofo"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to the register
      </Link>

      {!Number.isInteger(id) ? (
        <ErrorState
          title="That is not an occupation"
          description="That web address does not name an occupation."
        />
      ) : query.isError ? (
        <ErrorState
          title="That occupation did not load"
          description={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : query.isLoading || !occupation ? (
        <LoadingOccupation />
      ) : (
        <>
          <PageHeader
            title={occupation.title}
            description={`${occupation.code} · DHET register ${occupation.register_version}`}
          />
          {/* Two panels, never one form across both: the register half has no
              write route on the API at all. */}
          <div className="grid gap-6 xl:grid-cols-3">
            <div className="xl:col-span-1">
              <RegisterPanel occupation={occupation} />
            </div>
            <div className="xl:col-span-2">
              <AnnotationsPanel
                key={id}
                occupation={occupation}
                occupationId={id}
                canWrite={canWrite}
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}

function LoadingOccupation() {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading the occupation</span>
      <Skeleton className="mb-2 h-6 w-72" />
      <Skeleton className="mb-6 h-4 w-52" />
      <div className="grid gap-6 xl:grid-cols-3">
        {[1, 2].map((panel) => (
          <Card key={panel} className={panel === 1 ? "xl:col-span-1" : "xl:col-span-2"}>
            <CardContent className="space-y-4 py-6">
              {Array.from({ length: panel === 1 ? 6 : 10 }).map((_, line) => (
                <Skeleton key={line} className="h-4 w-full" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
